import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus, Check, X } from 'lucide-react';
import { sans, green, useTheme } from '../../tokens';
import {
  getHighlights, saveHighlights, subscribeToHighlightsChanges, newHighlightId,
} from '../../data/highlightsStore';

export default function KeyHighlights({ companyId, selectedPeriod, readOnly = false, defaults = [] }) {
  const t = useTheme();
  const [items, setItems] = useState(() => {
    const stored = getHighlights(companyId, selectedPeriod);
    return stored.length ? stored : defaults.map(text => ({ id: newHighlightId(), text }));
  });
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');

  // Re-hydrate when period or company changes, or when another tab edits.
  useEffect(() => {
    const stored = getHighlights(companyId, selectedPeriod);
    setItems(stored.length ? stored : defaults.map(text => ({ id: newHighlightId(), text })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, selectedPeriod]);
  useEffect(() => subscribeToHighlightsChanges(() => {
    const stored = getHighlights(companyId, selectedPeriod);
    if (stored.length) setItems(stored);
  }), [companyId, selectedPeriod]);

  const persist = (next) => {
    setItems(next);
    saveHighlights(companyId, selectedPeriod, next);
  };
  const startEdit = (item) => { setEditingId(item.id); setDraft(item.text); };
  const cancelEdit = () => { setEditingId(null); setDraft(''); };
  const saveEdit = () => {
    if (!draft.trim()) return;
    persist(items.map(i => i.id === editingId ? { ...i, text: draft.trim() } : i));
    cancelEdit();
  };
  const remove = (id) => persist(items.filter(i => i.id !== id));
  const addNew = () => {
    const item = { id: newHighlightId(), text: 'New highlight' };
    persist([...items, item]);
    startEdit(item);
  };
  const move = (idx, dir) => {
    const next = [...items];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    persist(next);
  };

  const inputStyle = {
    padding: '6px 10px', borderRadius: 4, border: `1px solid ${t.border}`,
    background: t.bgCard, color: t.textPrimary, fontFamily: sans, fontSize: 11,
    outline: 'none', width: '100%', boxSizing: 'border-box', resize: 'vertical',
  };

  return (
    <div style={{
      background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: '16px 24px', marginBottom: 0,
    }}>
      <div style={{
        fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.5, color: t.textMuted, fontFamily: sans, marginBottom: 10,
      }}>
        Key Highlights
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.length === 0 && (
          <div style={{ fontSize: 10, color: t.textFaint, fontFamily: sans, fontStyle: 'italic' }}>
            {readOnly ? 'No highlights for this period yet.' : 'No highlights yet — click "Add highlight" below.'}
          </div>
        )}
        {items.map((item, i) => {
          const isEditing = editingId === item.id;
          if (isEditing) {
            return (
              <div key={item.id} style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <textarea
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  rows={2}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                  style={inputStyle}
                />
                <button onClick={saveEdit} title="Save (⌘/Ctrl+Enter)" style={{ background: green, border: 'none', borderRadius: 4, cursor: 'pointer', color: '#fff', padding: 5 }}>
                  <Check size={12} />
                </button>
                <button onClick={cancelEdit} title="Cancel (Esc)" style={{ background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 4, cursor: 'pointer', color: t.textMuted, padding: 5 }}>
                  <X size={12} />
                </button>
              </div>
            );
          }
          return (
            <div key={item.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 10, color: green, fontWeight: 700, marginTop: 1, flexShrink: 0 }}>--</span>
              <span style={{ flex: 1, fontSize: 11, fontFamily: sans, color: t.textSecondary, lineHeight: 1.5 }}>
                {item.text}
              </span>
              {!readOnly && (
                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                  <button onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                    style={{ background: 'transparent', border: 'none', cursor: i === 0 ? 'not-allowed' : 'pointer', color: t.textMuted, padding: 2, opacity: i === 0 ? 0.3 : 1, fontSize: 10 }}>▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Move down"
                    style={{ background: 'transparent', border: 'none', cursor: i === items.length - 1 ? 'not-allowed' : 'pointer', color: t.textMuted, padding: 2, opacity: i === items.length - 1 ? 0.3 : 1, fontSize: 10 }}>▼</button>
                  <button onClick={() => startEdit(item)} title="Edit"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 2 }}>
                    <Pencil size={11} />
                  </button>
                  <button onClick={() => remove(item.id)} title="Delete"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#C0392B', padding: 2 }}>
                    <Trash2 size={11} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!readOnly && (
        <button onClick={addNew} style={{
          marginTop: 10, padding: '5px 12px', borderRadius: 6,
          border: `1px dashed ${t.border}`, background: 'transparent',
          color: t.textMuted, fontFamily: sans, fontSize: 10, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Plus size={11} /> Add highlight
        </button>
      )}
    </div>
  );
}
